// Croqui: `components/Icon.tsx`. Os 38 glifos do canvas, 24x24, agora em pixel (WEB-137): o
// desenho vem do mesmo lugar que `components/icons/` (pixelarticons vendorado + glifos à mão),
// então um nome daqui e o ícone equivalente de lá são o MESMO desenho. A API não mudou.
//
// Aqui ficam os ícones que os componentes do kit recebem por nome (`Btn`, `Stat`, `Empty`,
// `Notice`, `IconTile`). O teto de nitidez (abaixo de 14 px perde detalhe) está registrado em
// `components/icons/index.tsx`.

import { cn } from '../../lib/cn.ts';
import * as hand from '../icons/hand.ts';
import * as pax from '../icons/pixelarticons.gen.ts';

export type IconName =
  | 'grid'
  | 'swap'
  | 'wallet'
  | 'arrow'
  | 'key'
  | 'doc'
  | 'code'
  | 'webhook'
  | 'bolt'
  | 'book'
  | 'user'
  | 'shield'
  | 'percent'
  | 'lock'
  | 'bell'
  | 'users'
  | 'check'
  | 'ledger'
  | 'scale'
  | 'plug'
  | 'inbox'
  | 'cpu'
  | 'chart'
  | 'alert'
  | 'list'
  | 'gear'
  | 'search'
  | 'store'
  | 'sun'
  | 'moon'
  | 'logout'
  | 'dots'
  | 'ingot'
  | 'chevronDown'
  | 'chevronLeft'
  | 'chevronRight'
  | 'close'
  | 'copy';

const PATHS: Record<IconName, string> = {
  grid: pax.dashboard,
  swap: pax.sync,
  wallet: pax.wallet,
  arrow: pax.externalLink,
  key: hand.key,
  doc: pax.fileAlt,
  code: pax.code,
  webhook: hand.webhook,
  bolt: pax.zap,
  book: pax.bookOpen,
  user: pax.user,
  shield: hand.shieldCheck,
  percent: pax.percent,
  lock: pax.lock,
  bell: pax.notification,
  users: pax.users,
  check: hand.circleCheck,
  ledger: pax.book,
  scale: hand.scale,
  plug: hand.plug,
  inbox: pax.inbox,
  cpu: hand.cpu,
  chart: pax.trending,
  alert: hand.triangleAlert,
  list: pax.list,
  gear: pax.sliders,
  search: pax.search,
  store: pax.store,
  sun: pax.sunAlt,
  moon: pax.moon,
  logout: pax.logout,
  dots: pax.moreHorizontal,
  /* Lingotes da marca: um em cima de dois, o brilho é vazado (antes era um traço branco). */
  ingot: hand.ingot,
  chevronDown: pax.chevronDown,
  chevronLeft: pax.chevronLeft,
  chevronRight: pax.chevronRight,
  close: hand.closeBold,
  copy: pax.copy,
};

export const ICON_NAMES = Object.keys(PATHS) as IconName[];

/**
 * Ícone do kit. Decorativo por default (`aria-hidden`): quem carrega o significado é o
 * texto ao lado. Sozinho num botão, passe `label`, e o botão ganha `aria-label`
 * (`spec/17` §7 item 12).
 */
export function Icon({
  name,
  size = 18,
  label,
  className,
}: {
  name: IconName;
  size?: number;
  label?: string;
  className?: string;
}) {
  // Os dois ramos são escritos por extenso (e não um spread de atributos) porque o lint de
  // a11y do Biome (`noSvgWithoutTitle`) lê o JSX estaticamente: um `aria-hidden` vindo de
  // spread ele não enxerga.
  if (label === undefined) {
    return (
      <svg
        aria-hidden="true"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
        className={cn('shrink-0', className)}
      >
        <path d={PATHS[name]} />
      </svg>
    );
  }
  return (
    <svg
      role="img"
      aria-label={label}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cn('shrink-0', className)}
    >
      <title>{label}</title>
      <path d={PATHS[name]} />
    </svg>
  );
}
